import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, Paper, IconButton, Grid,
  Collapse, Table, TableBody, TableCell, TableHead, TableRow, Divider
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LagbasLogo from '../components/LagbasLogo';
import PeopleIcon from '@mui/icons-material/People';
import GroupIcon from '@mui/icons-material/Group';
import EditNoteIcon from '@mui/icons-material/EditNote';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase/config';
import { doc, getDoc } from 'firebase/firestore';

// ── Textblock ─────────────────────────────────────────────────────
const BASIC_INFO = [
  {
    heading: 'Vad är IUP?',
    text: 'LagbasN innehåller ett digitalt verktyg för Individuell Utvecklingsplan (IUP). Istället för lösa dokument och e-postbilagor samlas alla IUP-samtal på ett ställe — tillgängliga, spårbara och enkla att följa upp.',
  },
  {
    heading: 'Hur det fungerar',
    items: [
      'Du skapar ett nytt IUP-dokument inför varje samtal. Föregående svar följer automatiskt med som startpunkt — du utgår alltid från din nuvarande situation.',
      'Formuläret täcker sex bedömningskriterier, individuell målsättning och ömsesidiga åtaganden.',
      'När du är klar lämnar du in — din chef notifieras automatiskt i appen.',
      'Chefen kan läsa, kommentera och markera samtalet som genomfört.',
    ],
  },
  {
    heading: 'Fördelar',
    items: [
      'Alltid tillgängligt, inga dokument att leta efter',
      'Historik: alla tidigare samtal finns kvar och kan öppnas',
      'Tydlig status: Utkast → Inlämnad → Genomförd',
      'Notifieringar: rätt person aviseras automatiskt vid inlämning',
    ],
  },
];

const ROLE_ROWS = [
  { role: 'Medarbetare', iupType: 'Medarbetare-IUP', seesOthers: 'Nej', comments: 'Nej', creates: 'Nej' },
  { role: 'Gruppledare', iupType: 'Gruppledare-IUP', seesOthers: 'Sina medarbetare', comments: 'Ja', creates: 'Ja – för sitt team' },
  { role: 'AO-ansvarig', iupType: 'AO-ansvarig-IUP', seesOthers: 'Sina gruppledare', comments: 'Ja', creates: 'Ja – för sitt AO' },
  { role: 'VD', iupType: 'Alla typer', seesOthers: 'Alla AO-ansvariga', comments: 'Ja', creates: 'Ja' },
  { role: 'Superadmin', iupType: 'Alla typer', seesOthers: 'Alla', comments: 'Ja', creates: 'Ja' },
  { role: 'HR-ansvarig*', iupType: '—', seesOthers: '—', comments: '—', creates: 'Redigerar mallar' },
];

const IUP_TYPES = [
  { name: 'Medarbetare-IUP (v7.0)', desc: 'Sex kriterier med datum, bedömning och motivering. Individuell målsättning och ömsesidiga åtaganden.' },
  { name: 'Gruppledare-IUP (v4.0)', desc: 'Sex öppna reflektionsfrågor, tre positiva och tre negativa punkter, samt fritext.' },
  { name: 'AO-ansvarig-IUP', desc: 'Strategisk variant av GL-IUP. Synlig enbart för VD och superadmin.' },
];

const NOTIF_FLOW = [
  'Medarbetare lämnar in → Gruppledaren notifieras',
  'Gruppledare lämnar in → AO-ansvarig i samma affärsområde notifieras',
  'AO-ansvarig lämnar in → VD notifieras',
];

// ── Infosektion ───────────────────────────────────────────────────
function IUPInfo({ extended }) {
  const [open, setOpen] = useState(false);

  return (
    <Paper sx={{ mb: 3, overflow: 'hidden' }}>
      <Box
        onClick={() => setOpen(o => !o)}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1.5,
          px: 2.5, py: 1.5, cursor: 'pointer',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <InfoOutlinedIcon sx={{ color: '#FF6B00', fontSize: 20 }} />
        <Typography variant="subtitle2" fontWeight="bold" sx={{ flex: 1 }}>
          Om IUP-modulen i LagbasN
        </Typography>
        {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
      </Box>

      <Collapse in={open}>
        <Box sx={{ px: 2.5, pb: 2.5 }}>
          {/* Basblock – alla ser detta */}
          {BASIC_INFO.map(block => (
            <Box key={block.heading} sx={{ mb: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
                {block.heading}
              </Typography>
              {block.text && (
                <Typography variant="body2" color="text.secondary">{block.text}</Typography>
              )}
              {block.items && (
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  {block.items.map((item, i) => (
                    <li key={i}>
                      <Typography variant="body2" color="text.secondary">{item}</Typography>
                    </li>
                  ))}
                </Box>
              )}
            </Box>
          ))}

          {/* Utökat block – AO, VD, HR, superadmin */}
          {extended && (
            <>
              <Box sx={{ mt: 2, mb: 2 }}>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                  Rollbehörigheter
                </Typography>
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'action.hover' }}>
                        {['Roll', 'IUP-typ', 'Ser andras IUP', 'Kommenterar', 'Skapar åt andra'].map(h => (
                          <TableCell key={h} sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {ROLE_ROWS.map(row => (
                        <TableRow key={row.role} hover>
                          <TableCell sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>{row.role}</TableCell>
                          <TableCell sx={{ fontSize: '0.75rem' }}>{row.iupType}</TableCell>
                          <TableCell sx={{ fontSize: '0.75rem' }}>{row.seesOthers}</TableCell>
                          <TableCell sx={{ fontSize: '0.75rem' }}>{row.comments}</TableCell>
                          <TableCell sx={{ fontSize: '0.75rem' }}>{row.creates}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  * HR-ansvarig är ett ansvarsområde som kan kombineras med valfri org-nivå.
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                  IUP-typer
                </Typography>
                {IUP_TYPES.map(t => (
                  <Box key={t.name} sx={{ mb: 0.75 }}>
                    <Typography variant="body2" component="span" fontWeight="bold">{t.name}: </Typography>
                    <Typography variant="body2" component="span" color="text.secondary">{t.desc}</Typography>
                  </Box>
                ))}
              </Box>

              <Box>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                  Notifieringsflöde
                </Typography>
                <Box component="ol" sx={{ m: 0, pl: 2 }}>
                  {NOTIF_FLOW.map((step, i) => (
                    <li key={i}>
                      <Typography variant="body2" color="text.secondary">{step}</Typography>
                    </li>
                  ))}
                </Box>
              </Box>

              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
                  IUP-mallar (HR-ansvarig)
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Frågetexterna och kriterierna är redigerbara via <em>IUP-mallar</em>. HR-ansvarig kan
                  ändra formuleringar och lägga till egna kriterier/frågor utan kodändringar.
                  Ändringar träder i kraft för nya IUP-dokument.
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
}

// ── Huvudexport ───────────────────────────────────────────────────
export default function HR() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [userProfile, setUserProfile] = useState(null);

  useEffect(() => {
    if (!currentUser) return;
    getDoc(doc(db, 'users', currentUser.uid)).then(snap => {
      if (snap.exists()) setUserProfile(snap.data());
    });
  }, [currentUser]);

  const role = userProfile?.role;

  const isManager = role === 'gruppledare' ||
    role === 'ao_ansvarig' ||
    userProfile?.isDepartmentManager ||
    role === 'vd' ||
    role === 'superadmin';

  const isExpectationsAdmin = role === 'vd' ||
    role === 'ao_ansvarig' ||
    userProfile?.isDepartmentManager ||
    role === 'superadmin';

  const isHRorAdmin = userProfile?.isHRansvarig ||
    role === 'superadmin' ||
    role === 'vd';

  const isVDOrSuperadmin = role === 'vd' || role === 'superadmin';

  const showExtendedInfo = role === 'ao_ansvarig' ||
    userProfile?.isDepartmentManager ||
    role === 'vd' ||
    role === 'superadmin' ||
    userProfile?.isHRansvarig;

  const CARDS = [
    {
      label: 'Medarbetarregister',
      desc: 'Kontaktuppgifter, befattning, anställning — matar offert och signatur',
      icon: <PeopleIcon sx={{ fontSize: 36, color: '#FF6B00' }} />,
      path: '/employees',
      show: true,
      highlight: true,
    },
    {
      label: 'Mina IUP-samtal',
      desc: 'Se och skapa individuella utvecklingsplaner',
      icon: <PeopleIcon sx={{ fontSize: 36, color: '#FF6B00' }} />,
      path: '/hr/iup',
      show: !(role === 'vd') && !(role === 'superadmin' && !userProfile?.departmentId),
    },
    {
      label: 'Mitt team – IUP',
      desc: 'Teamöversikt och IUP-samtal för ditt team',
      icon: <GroupIcon sx={{ fontSize: 36, color: '#FF6B00' }} />,
      path: '/hr/team',
      show: isManager,
    },
    {
      label: 'IUP-mallar',
      desc: 'Redigera frågor och kriterier för IUP-formulären',
      icon: <EditNoteIcon sx={{ fontSize: 36, color: '#FF6B00' }} />,
      path: '/hr/iup-mall',
      show: isHRorAdmin,
    },
    {
      label: 'Mitt IUP',
      desc: isVDOrSuperadmin
        ? 'Mina mål, bolagsövergripande förväntningar och AI-matris'
        : 'Mina mål, affärsområdets förväntningar och AI-matris',
      icon: <TrackChangesIcon sx={{ fontSize: 36, color: '#FF6B00' }} />,
      path: '/hr/forvantan',
      show: isExpectationsAdmin,
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Box sx={{ bgcolor: '#1a1a1a', color: 'white', py: 1.5, px: 3,
        display: 'flex', alignItems: 'center', gap: 2 }}>
        <LagbasLogo />
        <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', mx: -0.5 }} />
        <IconButton color="inherit" size="small" onClick={() => navigate(-1)}>
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography variant="subtitle1" fontWeight="bold">Medarbetare</Typography>
          <Typography variant="caption" sx={{ color: '#aaa' }}>
            Medarbetarregister, IUP-samtal och medarbetarutveckling
          </Typography>
        </Box>
      </Box>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {CARDS.filter(c => c.show).map(card => (
            <Grid item xs={12} sm={6} md={4} key={card.path}>
              <Paper
                onClick={() => navigate(card.path)}
                sx={{ p: 3, cursor: 'pointer', '&:hover': { boxShadow: 4 },
                  transition: 'box-shadow .15s',
                  borderTop: '3px solid #FF6B00',
                  ...(card.highlight ? { bgcolor: '#fff8f4' } : {}) }}>
                <Box sx={{ mb: 1.5 }}>{card.icon}</Box>
                <Typography fontWeight="bold" sx={{ mb: 0.5 }}>{card.label}</Typography>
                <Typography variant="body2" color="text.secondary">{card.desc}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <IUPInfo extended={showExtendedInfo} />
      </Container>
    </Box>
  );
}
